import { Link } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import { type TutorialIndexItem } from "#app/lib/tutorials/loader";
import { cn } from "#app/utils/misc";

type Group = { title: string; items: TutorialIndexItem[] };

const categoryIcons: Record<string, string> = {
  "Start Here": "🚀",
  Beginner: "🧩",
  Intermediate: "🧠",
  Advanced: "🏁",
  Integrations: "🔌",
  "How-tos": "🛠️",
  Examples: "📦",
  Other: "📄",
  Library: "📚",
};

function groupTutorials(tutorials: TutorialIndexItem[]): Group[] {
  const groups: Record<string, Group> = {};
  const ensure = (key: string, title: string) => {
    if (!groups[key]) groups[key] = { title, items: [] };
    return groups[key]!;
  };

  for (const t of tutorials) {
    const primary = (t.slug.split("/")[0] || "other").toLowerCase();
    const secondary = (t.slug.split("/")[1] || "").toLowerCase();

    if (primary === "getting-started") {
      ensure("start-here", "Start Here").items.push(t);
      continue;
    }

    if (primary === "library") {
      if (secondary === "basic")
        ensure("level-beginner", "Beginner").items.push(t);
      else if (secondary === "intermediate")
        ensure("level-intermediate", "Intermediate").items.push(t);
      else if (secondary === "advanced")
        ensure("level-advanced", "Advanced").items.push(t);
      else if (secondary === "integrations")
        ensure("integrations", "Integrations").items.push(t);
      else ensure("library", "Library").items.push(t);
      continue;
    }

    if (primary === "how-tos") {
      ensure("how-tos", "How-tos").items.push(t);
      continue;
    }

    if (primary === "examples") {
      ensure("examples", "Examples").items.push(t);
      continue;
    }

    ensure("other", "Other").items.push(t);
  }

  const order = [
    "start-here",
    "level-beginner",
    "level-intermediate",
    "level-advanced",
    "integrations",
    "how-tos",
    "examples",
    "other",
  ];

  return order
    .map((k) => groups[k])
    .filter(Boolean)
    .map((g) => ({
      title: g!.title,
      items: g!.items,
    }));
}

export function TutorialSidebarNav({
  tutorials,
  currentSlug,
}: {
  tutorials: TutorialIndexItem[];
  currentSlug?: string;
}) {
  const { t } = useTranslation("docs");
  const groups = groupTutorials(tutorials);
  const labels: Record<string, string> = {
    "Start Here": t("categories.startHere"),
    Beginner: t("categories.beginner"),
    Intermediate: t("categories.intermediate"),
    Advanced: t("categories.advanced"),
    Integrations: t("categories.integrations"),
    Library: t("categories.library"),
    "How-tos": t("categories.howTos"),
    Examples: t("categories.examples"),
    Other: t("categories.other"),
  };

  return (
    <nav className="space-y-6">
      {groups.map((g) => (
        <div key={g.title} className="space-y-2">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <span className="text-sm">{categoryIcons[g.title] || "📄"}</span>
            {labels[g.title] ?? g.title}
          </div>
          <div className="space-y-0.5">
            {g.items.map((t) => {
              const isActive = currentSlug === t.slug;
              return (
                <Link
                  key={t.slug}
                  to={`/u/alpha/tutorials/${t.slug}`}
                  className={cn(
                    "block px-3 py-2 rounded-md text-sm transition-colors",
                    isActive
                      ? "relative text-[#535EA4] font-medium bg-[linear-gradient(90deg,#F0F4FF_0%,#FFFFFF_100%)] before:absolute before:left-0 before:top-1 before:bottom-1 before:w-1 before:rounded-full before:bg-[linear-gradient(180deg,#535EA4_0%,#909CCA_100%)] dark:bg-none dark:bg-zinc-800 dark:text-indigo-300"
                      : "text-gray-700 hover:bg-gray-50 hover:text-[#141A42] dark:text-zinc-300 dark:hover:bg-zinc-800",
                  )}
                  title={t.metadata.title}
                >
                  {t.metadata.title}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}
